"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.__testing = void 0;
exports.transformDsnProject = transformDsnProject;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const TransformOrchestrator_1 = require("../../../Shared/HierarchicalTransformer/TransformOrchestrator");
const TransformProject_1 = require("../../../Shared/HierarchicalTransformer/TransformObjects/TransformProject");
const TransformImageExtraction_1 = require("../../../Shared/HierarchicalTransformer/TransformObjects/TransformImageExtraction");
const OrcadDsnTransformProjectGenerator_1 = require("./Generators/OrcadDsnTransformProjectGenerator");
const OrcadDsnTransformSchematicGenerator_1 = require("./Generators/OrcadDsnTransformSchematicGenerator");
const OrcadDsnTransformModuleGenerator_1 = require("./Generators/OrcadDsnTransformModuleGenerator");
const OrcadDsnTransformComponentGenerator_1 = require("./Generators/OrcadDsnTransformComponentGenerator");
const OrcadDsnTransformNetWireGenerator_1 = require("./Generators/OrcadDsnTransformNetWireGenerator");
const OrcadDsnTransformPortGenerator_1 = require("./Generators/OrcadDsnTransformPortGenerator");
const OrcadDsnTransformSheetSymbolGenerator_1 = require("./Generators/OrcadDsnTransformSheetSymbolGenerator");
const OrcadDsnTransformOffPageConnectorGenerator_1 = require("./Generators/OrcadDsnTransformOffPageConnectorGenerator");
const OrcadDsnTransformGraphicsGenerator_1 = require("./Generators/OrcadDsnTransformGraphicsGenerator");
const OrcadDsnTransformGlobalSymbolGenerator_1 = require("./Generators/OrcadDsnTransformGlobalSymbolGenerator");
const OrcadNetProperties_1 = require("./OrcadNetProperties");
function transformDsnProject(parsedDef, options) {
    const transformer = new TransformOrchestrator_1.TransformOrchestrator({
        generators: [
            new OrcadDsnTransformProjectGenerator_1.OrCADDsnTransformProjectGenerator(),
            new OrcadDsnTransformSchematicGenerator_1.OrCadDsnTransformSchematicGenerator(),
            new OrcadDsnTransformModuleGenerator_1.OrCadDsnTransformModuleGenerator(),
            new OrcadDsnTransformNetWireGenerator_1.OrcadDsnTransformNetWireGenerator(),
            new OrcadDsnTransformPortGenerator_1.OrcadDsnTransformPortGenerator(),
            new OrcadDsnTransformSheetSymbolGenerator_1.OrcadDsnTransformSheetSymbolGenerator(),
            new OrcadDsnTransformOffPageConnectorGenerator_1.OrcadDsnTransformOffPageConnectorGenerator(),
            new OrcadDsnTransformGraphicsGenerator_1.OrcadDsnTransformGraphicsGenerator(),
            new OrcadDsnTransformGlobalSymbolGenerator_1.OrcadDsnTransformGlobalSymbolGenerator(),
            new OrcadDsnTransformComponentGenerator_1.OrCadDsnTransformComponentGenerator(),
        ],
    });
    const rootContext = {
        parsedDef,
        options,
    };
    const result = transformer.transform(rootContext, TransformProject_1.TransformProject);
    for (const project of transformer.getRegistry().getPrimaries(TransformProject_1.TransformProject)) {
        (0, OrcadNetProperties_1.emitOrcadNetProperties)(project.compiled, project.resolvedNetGroups, (message) => {
            project.compiled.processingResults.innerResults.push({
                type: 'orcad-net-property',
                message,
                severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Warning,
            });
        });
    }
    for (let projectIndex = 0; projectIndex < result.length; projectIndex++) {
        const project = result[projectIndex];
        for (const message of rootContext.hierarchyPortDiagnosticSets?.[projectIndex] ?? []) {
            project.processingResults.innerResults.push({
                type: 'orcad-dsn-transformer',
                message,
                severity: sigma_engine_shared_1.DesignModels.BoundaryLevel.Warning,
            });
        }
        (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(project.processingResults);
    }
    if (rootContext.designHierarchyResults) {
        for (let i = 0; i < result.length && i < rootContext.designHierarchyResults.length; i++) {
            result[i].designHierarchy = rootContext.designHierarchyResults[i];
        }
    }
    const imageExtractionObjects = transformer.getRegistry().getPrimaries(TransformImageExtraction_1.TransformImageExtraction);
    const extractedImages = imageExtractionObjects.map((obj) => obj.compiled);
    const { detectedRootSourceFiles } = rootContext;
    if (detectedRootSourceFiles === undefined) {
        throw new Error('OrCAD transform finished without recording the detected root source files');
    }
    return {
        projectModels: result,
        extractedImages,
        rootSourceFiles: detectedRootSourceFiles,
        ...collectFlatNetBindings(rootContext, transformer.getRegistry().getPrimaries(TransformProject_1.TransformProject)),
    };
}
const BusDimensionName = 'bus';
function busPositionKey(position) {
    return `${position[0].toFixed(4)},${position[1].toFixed(4)}`;
}
function isComponentPinConnection(connection) {
    return connection.appliesNetName?.labelType !== 'global';
}
const ForcedUniquenessSuffixPattern = /_\d+$/;
function labelSpellsResolvedName(label, resolvedName) {
    const normalizedLabel = label.toUpperCase();
    const normalizedResolved = resolvedName.toUpperCase();
    if (normalizedLabel === normalizedResolved) {
        return true;
    }
    const beforeTieBreak = normalizedResolved.replace(ForcedUniquenessSuffixPattern, '');
    return beforeTieBreak !== normalizedResolved && beforeTieBreak === normalizedLabel;
}
function netNameConnectsDesignWide(net, resolvedName) {
    if (net.labels.some((label) => label.labelType === 'global' &&
        (labelSpellsResolvedName(label.formattedLabel ?? label.originalLabel, resolvedName) ||
            labelSpellsResolvedName(label.originalLabel, resolvedName)))) {
        return true;
    }
    return net.connections.some(({ connection }) => {
        const applies = connection.appliesNetName;
        if (!applies || !labelSpellsResolvedName(applies.label, resolvedName)) {
            return false;
        }
        return applies.labelType === 'global' || applies.labelType === 'power-pin';
    });
}
class FlatNetUnion {
    constructor() {
        this.parents = [];
    }
    add() {
        this.parents.push(this.parents.length);
        return this.parents.length - 1;
    }
    find(id) {
        let root = id;
        while (this.parents[root] !== root) {
            root = this.parents[root] ?? root;
        }
        let walk = id;
        while (this.parents[walk] !== root) {
            const next = this.parents[walk] ?? root;
            this.parents[walk] = root;
            walk = next;
        }
        return root;
    }
    union(left, right) {
        const leftRoot = this.find(left);
        const rightRoot = this.find(right);
        if (leftRoot !== rightRoot) {
            this.parents[leftRoot] = rightRoot;
        }
    }
}
function registerFlatNetName(candidates, design, netName, id, connects) {
    if (!connects) {
        return;
    }
    const byName = candidates.byDesignAndName.get(design) ?? new Map();
    const entry = byName.get(netName) ?? { connectingCandidateIds: [] };
    entry.connectingCandidateIds.push(id);
    byName.set(netName, entry);
    candidates.byDesignAndName.set(design, byName);
}
function flatNetCandidates(projects) {
    const candidates = {
        union: new FlatNetUnion(),
        ids: new Map(),
        byDesignAndName: new Map(),
        unresolved: new Map(),
    };
    for (const project of projects) {
        for (const group of project.resolvedNetGroups) {
            const id = candidates.union.add();
            const name = group.physicalName;
            if (!name) {
                candidates.unresolved.set(id, 'the naming rules resolved no physical name for the group it belongs to');
            }
            for (const [instance, nets] of group.getNets()) {
                const design = instance.hierarchyEntry?.design;
                for (const net of nets) {
                    candidates.ids.set(net, id);
                    if (name && design) {
                        registerFlatNetName(candidates, design, name, id, netNameConnectsDesignWide(net, name));
                    }
                }
            }
        }
    }
    return candidates;
}
function collectFlatNetBindings(rootContext, projects) {
    const candidates = flatNetCandidates(projects);
    const busMembers = [];
    const pageNetBindings = [];
    const portNetBindings = [];
    const offPageConnectorNetBindings = [];
    const uncompiledNetObjects = [];
    const pending = [];
    for (const record of rootContext.pageSchematicRecords ?? []) {
        if (!record.schematic.isCompiled) {
            uncompiledNetObjects.push({ page: record.page });
            continue;
        }
        const instance = record.schematic.instance;
        const parent = instance?.path[instance.path.length - 1];
        const design = record.schematic.hierarchyEntry.design;
        const entry = {
            occurrence: {
                page: record.page,
                designId: record.schematic.compiled.designId,
                occurrencePath: instance?.pathUniqueId ?? '',
                ...(parent === undefined ? {} : { parentOccurrencePath: parent.pathUniqueId }),
                ...(instance?.sheetSymbol === undefined ? {} : { sheetSymbolDbId: instance.sheetSymbol.DbId }),
                ...(instance?.instOccurence === undefined ? {} : { occurrence: instance.instOccurence }),
            },
            atComponentPins: [],
            all: [],
            ports: new Map(),
            connectors: new Map(),
        };
        pending.push(entry);
        const memberNetsAtPosition = new Map();
        const busNetsAtPosition = new Map();
        const memberPositions = new Map();
        for (const net of record.schematic.netCombobulator.iterateNets()) {
            let flatNetId = candidates.ids.get(net);
            if (flatNetId === undefined) {
                flatNetId = candidates.union.add();
                candidates.ids.set(net, flatNetId);
                const netName = String(net.name);
                registerFlatNetName(candidates, design, netName, flatNetId, netNameConnectsDesignWide(net, netName));
                if (net.labels.some((label) => label.labelType === 'hierarchical-port' || label.labelType === 'off-page-connector')) {
                    candidates.unresolved.set(flatNetId, `no grouping rule claimed the net "${String(net.name)}" a connector sits on`);
                }
            }
            entry.all.push(flatNetId);
            if (net.connections.some(({ connection }) => isComponentPinConnection(connection))) {
                entry.atComponentPins.push(flatNetId);
            }
            for (const object of net.netObjects) {
                for (const endpoint of object.endpoints) {
                    const key = busPositionKey(endpoint.position);
                    if (endpoint.dimension === BusDimensionName) {
                        const busIds = busNetsAtPosition.get(key) ?? [];
                        busIds.push(flatNetId);
                        busNetsAtPosition.set(key, busIds);
                        continue;
                    }
                    const flatNetIds = memberNetsAtPosition.get(key) ?? [];
                    flatNetIds.push(flatNetId);
                    memberNetsAtPosition.set(key, flatNetIds);
                    const member = memberPositions.get(flatNetId) ?? { memberName: String(net.name), positions: [] };
                    member.positions.push(key);
                    memberPositions.set(flatNetId, member);
                }
            }
        }
        for (const [candidateId, member] of memberPositions) {
            for (const position of member.positions) {
                for (const busCandidateId of busNetsAtPosition.get(position) ?? []) {
                    busMembers.push({ busCandidateId, memberName: member.memberName, candidateId });
                }
            }
        }
        for (const net of record.schematic.netCombobulator.iterateNets()) {
            const flatNetId = candidates.ids.get(net);
            if (flatNetId === undefined) {
                continue;
            }
            const objectFlatNetIds = [flatNetId];
            for (const object of net.netObjects) {
                for (const endpoint of object.endpoints) {
                    if (endpoint.dimension !== BusDimensionName) {
                        continue;
                    }
                    for (const memberFlatNetId of memberNetsAtPosition.get(busPositionKey(endpoint.position)) ?? []) {
                        objectFlatNetIds.push(memberFlatNetId);
                    }
                }
            }
            for (const label of net.labels) {
                const portDbId = label.strategyMetadata?.['hierarchical-port']?.portDbId;
                if (portDbId !== undefined) {
                    const ids = entry.ports.get(Number(portDbId)) ?? [];
                    for (const objectFlatNetId of objectFlatNetIds) {
                        ids.push(objectFlatNetId);
                    }
                    entry.ports.set(Number(portDbId), ids);
                }
                const connectorDbId = label.strategyMetadata?.['off-page-connector']?.connectorDbId;
                if (connectorDbId !== undefined) {
                    const ids = entry.connectors.get(Number(connectorDbId)) ?? [];
                    for (const objectFlatNetId of objectFlatNetIds) {
                        ids.push(objectFlatNetId);
                    }
                    entry.connectors.set(Number(connectorDbId), ids);
                }
            }
        }
    }
    for (const byName of candidates.byDesignAndName.values()) {
        for (const entry of byName.values()) {
            for (const id of entry.connectingCandidateIds.slice(1)) {
                candidates.union.union(entry.connectingCandidateIds[0], id);
            }
        }
    }
    const membersByBusAndName = new Map();
    for (const member of busMembers) {
        const key = JSON.stringify([candidates.union.find(member.busCandidateId), member.memberName]);
        const ids = membersByBusAndName.get(key) ?? [];
        ids.push(member.candidateId);
        membersByBusAndName.set(key, ids);
    }
    for (const ids of membersByBusAndName.values()) {
        for (const id of ids.slice(1)) {
            candidates.union.union(ids[0], id);
        }
    }
    for (const record of rootContext.portNetLabelRecords ?? []) {
        if (!record.label.isCompiled) {
            uncompiledNetObjects.push({ page: record.page, portDbId: record.portDbId });
        }
    }
    const resolve = (ids) => new Set(ids.map((id) => candidates.union.find(id)));
    for (const entry of pending) {
        pageNetBindings.push({
            ...entry.occurrence,
            flatNetIdsAtComponentPins: resolve(entry.atComponentPins),
            flatNetIds: resolve(entry.all),
        });
        for (const [portDbId, ids] of entry.ports) {
            portNetBindings.push({ ...entry.occurrence, portDbId, flatNetIds: resolve(ids) });
        }
        for (const [connectorDbId, ids] of entry.connectors) {
            offPageConnectorNetBindings.push({ ...entry.occurrence, connectorDbId, flatNetIds: resolve(ids) });
        }
    }
    const unresolvedFlatNets = new Map();
    for (const [id, reason] of candidates.unresolved) {
        unresolvedFlatNets.set(candidates.union.find(id), reason);
    }
    return {
        portNetBindings,
        offPageConnectorNetBindings,
        pageNetBindings,
        uncompiledNetObjects,
        unresolvedFlatNets: Array.from(unresolvedFlatNets, ([flatNetId, reason]) => ({ flatNetId, reason })),
    };
}
exports.__testing = { netNameConnectsDesignWide };
//# sourceMappingURL=OrcadDsnOrchestratedTransformer.js.map